import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Star, Hammer, Paintbrush, Bath, Home, Layers } from "lucide-react";

const HomePage = () => {
  const services = [
  {
    icon: Home,
    title: "Kitchen Renovation",
    description: "Transform your kitchen into a modern, functional space with custom cabinets, countertops, and more.",
    image: "https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/741kiolr_1000115910.jpg"
  },
  {
    icon: Bath,
    title: "Bathroom Remodeling",
    description: "Create your spa-like retreat with luxury fixtures, custom tile work, and elegant designs.",
    image: "https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/mji8od69_1000109913.jpg"
  },
  {
    icon: Hammer,
    title: "General Contracting",
    description: "Skilled oversight of all aspects of construction to ensure efficiency and quality.",
    image: "https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/hv43d69t_Gemini_Generated_Image_nwkojrnwkojrnwko.png"
  }];


  const testimonials = [
  {
    name: "Graeme Kemp",
    role: "Bathroom Renovation",
    content: "We recently redid our entire bathroom and found Johann and Ephraim to be incredibly helpful and accommodating while still finishing the job on time and to a high degree of quality. When we opened up the floor, we found many issues that needed to be addressed. They worked overtime to ensure the floor was fixed properly, solid and still on time. We would recommend them to anyone!",
    rating: 5
  },
  {
    name: "Patrick Vallee",
    role: "Tile Installation",
    content: "They installed floor tiles in my bathroom and entry way. The work was fast, professionally done with a quality finish. Would definitely recommend JN Nickel Contracting for any tile work you need done!",
    rating: 5
  },
  {
    name: "Brian Gibson",
    role: "Home Renovation",
    content: "Very happy with the work that was done. They made sure that the job was done to the best standards. Thanks for a job well done.",
    rating: 5
  }];


  const stats = [
  { value: "100+", label: "Projects Completed" },
  { value: "3", label: "Years Experience" },
  { value: "100%", label: "Satisfaction Rate" },
  { value: "50+", label: "5-Star Reviews" }];


  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1760072513367-55182245e76c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBob21lJTIwaW50ZXJpb3IlMjByZW5vdmF0aW9ufGVufDB8fHx8MTc3MTk1ODk3OXww&ixlib=rb-4.1.0&q=85')"
          }} />

        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20">
          <div className="max-w-3xl">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4 animate-fade-in-up">
              Quality Craftsmanship Since 2022
            </p>
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold text-white uppercase tracking-tight mb-6 animate-fade-in-up animation-delay-100">
              Building Dreams,<br />
              <span className="text-[#d97706]">One Room at a Time</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-xl animate-fade-in-up animation-delay-200">
              Transform your home with expert renovation services. From kitchens to complete home makeovers, we bring your vision to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-[#d97706] hover:bg-[#b45309] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 px-8 btn-glow"
                  data-testid="hero-cta-btn">

                  Get Free Estimate
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/gallery">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0f172a] font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 px-8"
                  data-testid="hero-gallery-btn">

                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0f172a] py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) =>
            <div key={index} className="text-center">
                <p className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#d97706]">
                  {stat.value}
                </p>
                <p className="text-slate-400 mt-2 uppercase tracking-wider text-sm">
                  {stat.label}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              What We Offer
            </p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#0f172a] uppercase tracking-tight">
              Our Expertise
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) =>
            <div
              key={index}
              className="group bg-white border border-slate-100 overflow-hidden service-card"
              data-testid={`service-card-${index}`}>

                <div className="aspect-[4/3] overflow-hidden">
                  <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />

                </div>
                <div className="p-8 border-t-4 border-t-transparent group-hover:border-t-[#d97706] transition-colors">
                  <service.icon className="w-10 h-10 text-[#d97706] mb-4" />
                  <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0f172a] uppercase mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-[#0f172a] text-[#0f172a] hover:bg-[#0f172a] hover:text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 px-8"
                data-testid="view-all-services-btn">

                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
                Why Choose Us
              </p>
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#0f172a] uppercase tracking-tight mb-8">
                Excellence in Every Detail
              </h2>
              <div className="space-y-6">
                {[
                "Meticulous planning & attention to detail",
                "Clear communication throughout every project",
                "On time and within budget delivery",
                "Licensed, bonded & fully insured",
                "Tailored services for your unique needs",
                "Expectations not just met, but exceeded"].
                map((item, index) =>
                <div key={index} className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-[#d97706] flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-slate-700">{item}</span>
                  </div>
                )}
              </div>
              <div className="mt-10">
                <Link to="/about">
                  <Button
                    size="lg"
                    className="bg-[#0f172a] hover:bg-[#1e293b] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 px-8"
                    data-testid="learn-more-btn">

                    Learn More About Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/dz71v1bt_20260322_191334.jpg"
                alt="Johann Nickel"
                className="w-full aspect-[4/3] object-cover" />

              <div className="absolute -bottom-6 -left-6 bg-[#d97706] text-white p-6">
                <p className="font-['Barlow_Condensed'] text-4xl font-bold">10+</p>
                <p className="uppercase tracking-wider text-sm">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-[#f1f5f9]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              Client Testimonials
            </p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#0f172a] uppercase tracking-tight">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) =>
            <div
              key={index}
              className="bg-white p-8 border-l-4 border-[#d97706] testimonial-card"
              data-testid={`testimonial-card-${index}`}>

                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) =>
                <Star key={i} className="w-5 h-5 fill-[#d97706] text-[#d97706]" />
                )}
                </div>
                <p className="text-slate-700 leading-relaxed mb-6 text-lg">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-['Barlow_Condensed'] font-bold text-[#0f172a] text-lg uppercase">
                    {testimonial.name}
                  </p>
                  <p className="text-[#d97706] text-sm">{testimonial.role}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Get a free, no-obligation estimate for your renovation project. Our team is ready to bring your vision to life.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#d97706] hover:bg-[#b45309] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 px-10 btn-glow"
              data-testid="cta-get-quote-btn">

              Get Your Free Quote Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>);

};

export default HomePage;